<template>
  <section class="relative w-full h-[85vh] min-h-[600px] overflow-hidden flex flex-col justify-end">
    
    <div class="absolute top-24 right-6 z-50" v-if="isEditMode">
      <button
        @click="editHero"
        class="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white bg-black/40 backdrop-blur-md border border-white/20 rounded-full hover:bg-white hover:text-black transition-all"
      >
        <i class="fa-solid fa-pen-to-square"></i>
        <span>Éditer</span>
      </button>
    </div>

    <div class="absolute inset-0 z-0">
      <div v-if="loading" class="w-full h-full bg-ink-800 animate-pulse"></div>

      <img
        v-else-if="resolvedImageUrl"
        :src="resolvedImageUrl"
        alt="Hero background"
        decoding="async"
        loading="eager"
        @load="imageLoaded = true"
        class="w-full h-full object-cover object-right transition-all duration-1000"
        :class="imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'"
      />

      <div v-else class="w-full h-full bg-gradient-to-br from-ink-900 to-ink-800"></div>

      <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90"></div>
    </div>

    <div class="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-16 lg:pb-64 flex flex-col items-end">
      
      <div class="w-full max-w-3xl text-right">
        <div v-if="loading" class="flex flex-col items-end space-y-4 animate-pulse">
          <div class="h-12 w-3/4 bg-white/20 rounded-lg"></div>
          <div class="h-6 w-1/2 bg-white/20 rounded-lg"></div>
          <div class="mt-6 flex gap-3">
             <div class="h-12 w-32 bg-white/20 rounded-full"></div>
          </div>
        </div>

        <template v-else>
          <h1
            class="font-extrabold text-white tracking-tight leading-tight drop-shadow-lg"
            :style="{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }"
          >
            {{ titreAffiche }}
          </h1>

          <p class="mt-6 text-lg sm:text-xl text-white/90 font-medium leading-relaxed ml-auto max-w-xl drop-shadow-md">
            {{ descriptionAffiche }}
          </p>

          <div class="mt-10 flex flex-col sm:flex-row items-center justify-end gap-4">
            <template v-for="(bouton, index) in heroButtons" :key="bouton.id || index">
              
              <component
                :is="isExternal(bouton.action) ? 'a' : 'router-link'"
                :href="isExternal(bouton.action) ? bouton.action : undefined"
                :to="!isExternal(bouton.action) ? localized(bouton.action) : undefined"
                :target="isExternal(bouton.action) ? '_blank' : undefined"
                class="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                :class="index === 0 
                  ? 'bg-white text-ink-900 hover:bg-brand-50 hover:text-brand-700 shadow-[0_0_20px_rgba(255,255,255,0.3)]' 
                  : 'bg-white/10 text-white backdrop-blur-sm border border-white/30 hover:bg-white/20'"
              >
                <span>{{ bouton.label || (getLangue === 'en' ? 'Discover' : 'Découvrir') }}</span>
                <i :class="isExternal(bouton.action) ? 'fa-solid fa-arrow-up-right-from-square' : 'fas fa-arrow-right'" 
                   class="text-xs transition-transform group-hover:translate-x-1"></i>
              </component>

            </template>
          </div>
        </template>
      </div>
    </div>

  </section>
</template>

<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

// Base d’URL issue de l’instance axios centralisée
const API_BASE = String(process.env.VUE_APP_API_BASE || "").replace(/\/+$/, "");
const UPLOADS_ORIGIN = String(process.env.VUE_APP_UPLOADS_ORIGIN || API_BASE).replace(/\/+$/, "");


export default {
  name: "HeroComponent",
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
  },
  data() {
    return {
      heroContent: null,
      heroButtons: [],
      heroImage: null, // { image_url? | url? | path? }
      loading: true,
      imageLoaded: false,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),

    titreAffiche() {
      return this.heroContent?.titre || (this.getLangue === "en" ? "Welcome" : "Bienvenue");
    },
    descriptionAffiche() {
      return (
        this.heroContent?.description ||
        (this.getLangue === "en"
          ? "Your ideal partner to make your projects succeed."
          : "Votre partenaire idéal pour réussir vos projets.")
      );
    },

    // URL finale résolue pour l’image
    resolvedImageUrl() {
      const raw =
        this.heroImage?.image_url ||
        this.heroImage?.url ||
        this.heroImage?.path ||
        "";

      if (!raw) return "";

      // Si c’est un /uploads/..., préfixe avec UPLOADS_ORIGIN
      if (/^\/uploads\//i.test(raw)) {
        const base = String(UPLOADS_ORIGIN || "").replace(/\/+$/, "");
        return base ? `${base}${raw}` : raw;
      }

      // Si c’est déjà absolu (http/https), renvoie tel quel
      if (/^https?:\/\//i.test(raw)) return raw;

      // Sinon: URL relative → préfixe API_BASE
      const base = String(API_BASE || "").replace(/\/+$/, "");
      return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
    },

    hasImage() {
      return !!this.resolvedImageUrl;
    },

    // Style de fond: image si dispo, sinon dégradé
    bgStyle() {
      if (this.hasImage) {
        return { backgroundImage: `url(${this.resolvedImageUrl})` };
      }
      return { backgroundImage: "linear-gradient(135deg, #eaf1ff, #f5f7ff)" };
    },

    // Gestion de l’opacité / fade-in uniquement si image
    bgClass() {
      if (!this.hasImage) return "opacity-100";
      return this.imageLoaded ? "opacity-100" : "opacity-0";
    },
  },
  watch: {
    getLangue(newLang) {
      this.loadData(newLang);
    },
    resolvedImageUrl(newUrl, oldUrl) {
      if (newUrl !== oldUrl) {
        // (re)précharge la nouvelle image et ne montre qu'une fois prête
        this.preloadImage(newUrl);
      }
    },
  },
  methods: {
    isExternal(href) {
      return /^https?:\/\//i.test(String(href || ""));
    },
    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    localized(path) {
      return this.ensureLocalePrefix(path, this.getLangue);
    },

    preloadImage(src) {
      if (!src) {
        this.imageLoaded = true; // pas d'image -> gradient visible
        return;
      }
      this.imageLoaded = false; // on repart en fade-out

      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.onload = () => {
        // une frame plus tard pour garantir la transition
        requestAnimationFrame(() => { this.imageLoaded = true; });
      };
      img.onerror = () => { this.imageLoaded = true; }; // fallback: montre le gradient
      img.src = src;
    },

    async resolvePageId(lang) {
      const direct = Number(this.pageId);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const resolved = await fetchPageIdByCode(this.pageCode || "home", lang || "fr");
      const n = Number(resolved?.page_id ?? resolved?.id ?? resolved);
      return Number.isFinite(n) && n > 0 ? n : null;
    },

    // async loadData(lang) {
    //   this.loading = true;
    //   try {
    //     const pageId = await this.resolvePageId(lang);
    //     if (!pageId) {
    //       this.heroContent = null;
    //       this.heroButtons = [];
    //       this.heroImage = null;
    //       // pas d'image -> on montre direct le gradient
    //       this.imageLoaded = true;
    //       this.loading = false;
    //       return;
    //     }

    //     // Utilise le client HTTP centralisé (baseURL = /api ou VUE_APP_API_BASE)
    //     const { data } = await http.get("hero", { params: { page_id: pageId } });

    //     const title = data?.title || null;
    //     const buttons = Array.isArray(data?.buttons) ? data.buttons : [];
    //     const images = Array.isArray(data?.images) ? data.images : [];

    //     this.heroContent = title
    //       ? { id: title.id, titre: title.titre || "", description: title.description || "" }
    //       : null;

    //     this.heroButtons = buttons.map((b, idx) => ({
    //       id: b.id,
    //       contenu_id: b.contenu_id,
    //       label: b.label || (this.getLangue === "en" ? "Discover" : "Découvrir"),
    //       action: b.action || "#",
    //       style: b.style || (idx === 0 ? "primary" : "secondary"),
    //     }));

    //     this.heroImage = images.length ? images[0] : null;

    //     // ne touche pas imageLoaded ici : le watcher resolvedImageUrl lancera preloadImage()
    //     if (!this.hasImage) this.imageLoaded = true; // gradient visible si pas d'image
    //   } catch (error) {
    //     console.error("[HeroComponent] loadData error:", error);
    //     this.heroContent = null;
    //     this.heroButtons = [];
    //     this.heroImage = null;
    //     this.imageLoaded = true; // évite rester en opacity-0
    //   } finally {
    //     this.loading = false;
    //   }
    // },
async loadData(lang) {
      // 🛡️ SÉCURITÉ : On nettoie et on vérifie la langue
      const l = String(lang || this.getLangue || this.$route.params.langue || "").trim();

      // Si pas de langue, on arrête tout de suite. 
      // Le "watch: { getLangue }" relancera cette fonction dès que la langue sera disponible.
      if (!l || l === "undefined" || l === "null") {
        return;
      }

      this.loading = true;
      try {
        // On utilise la variable 'l' validée
        const pageId = await this.resolvePageId(l);
        
        if (!pageId) {
          this.heroContent = null;
          this.heroButtons = [];
          this.heroImage = null;
          this.imageLoaded = true;
          this.loading = false;
          return;
        }

        const { data } = await http.get("hero", { params: { page_id: pageId } });

        const title = data?.title || null;
        const buttons = Array.isArray(data?.buttons) ? data.buttons : [];
        const images = Array.isArray(data?.images) ? data.images : [];

        this.heroContent = title
          ? { id: title.id, titre: title.titre || "", description: title.description || "" }
          : null;

        this.heroButtons = buttons.map((b, idx) => ({
          id: b.id,
          contenu_id: b.contenu_id,
          label: b.label || (l === "en" ? "Discover" : "Découvrir"), // Utilise 'l' ici aussi
          action: b.action || "#",
          style: b.style || (idx === 0 ? "primary" : "secondary"),
        }));

        this.heroImage = images.length ? images[0] : null;

        if (!this.hasImage) this.imageLoaded = true;
      } catch (error) {
        // Optionnel : ignorer les erreurs 400 silencieusement
        if (error.response && error.response.status !== 400) {
           console.error("[HeroComponent] loadData error:", error);
        }
        this.heroContent = null;
        this.heroButtons = [];
        this.heroImage = null;
        this.imageLoaded = true;
      } finally {
        this.loading = false;
      }
    },
    editHero() {
      this.$emit("edit", {
        heroTitle: this.heroContent,
        heroButtons: this.heroButtons,
        heroImage: this.heroImage,
      });
    },

    buttonClass(bouton, index) {
      const isPrimary = (bouton.style || (index === 0 ? "primary" : "secondary")) === "primary";
      return isPrimary
        ? "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-300"
        : "bg-white text-ink-800 border border-ink-200 hover:bg-ink-50 focus:ring-ink-300";
    },
  },
  mounted() {
    this.loadData(this.getLangue);
  },
};
</script>

<style scoped>
/* Petit effet pour rendre le texte encore plus lisible si besoin */
h1, p {
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

@media (prefers-reduced-motion: reduce) {
  .transition-all {
    transition: none !important;
  }
}
</style>